import React from 'react';
import { motion } from 'framer-motion';
import { useFinancial } from '../context/FinancialContext';
import { calculateRetirementMetrics, generateSIPRecommendations, calculatePerformanceScenarios, calculateTotalWhatIfRequirements } from '../utils/calculations';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PDFExport from './PDFExport';
import AIInsights from './AIInsights';

const { FiFileText, FiAlertTriangle, FiCheckCircle, FiTarget, FiDollarSign, FiTrendingUp, FiCreditCard, FiInfo, FiShield, FiActivity, FiPercent } = FiIcons;

function Summary() {
  const { state } = useFinancial();
  
  const totalSIPRequired = state.goals.reduce((sum, goal) => sum + (goal.requiredSIP || 0), 0);
  const totalLumpSumRequired = state.goals.reduce((sum, goal) => sum + (goal.requiredLumpSum || 0), 0);
  const totalTaggedInvestments = state.goals.reduce((sum, goal) => sum + (goal.tagExistingInvestments ? goal.existingInvestmentValue || 0 : 0), 0);
  
  const retirementMetrics = calculateRetirementMetrics(state.retirementPlan, state.userProfile);
  const totalMonthlySIP = totalSIPRequired + (retirementMetrics.requiredSIP || 0);
  const totalMonthlyCommitment = totalMonthlySIP;
  const monthlySurplus = state.userProfile.monthlyIncome - state.userProfile.monthlyExpenses;
  
  const goalsOnTrack = state.goals.filter(goal => goal.achievementProbability >= 90);
  const goalsNeedingAttention = state.goals.filter(goal => goal.achievementProbability >= 70 && goal.achievementProbability < 90);
  const goalsAtRisk = state.goals.filter(goal => goal.achievementProbability < 70);
  
  const isOverBudget = totalMonthlyCommitment > monthlySurplus;

  // Calculate Net Worth
  const calculateNetWorth = () => {
    const totalAssets = 
      Object.values(state.userProfile.existingInvestments || {}).reduce((sum, val) => sum + (val || 0), 0) + 
      Object.values(state.userProfile.assets || {}).reduce((sum, val) => sum + (val || 0), 0);
    
    const totalLiabilities = Object.values(state.userProfile.liabilities || {}).reduce((sum, val) => sum + (val || 0), 0);
    
    return totalAssets - totalLiabilities;
  };
  
  const netWorth = calculateNetWorth();

  // Calculate insurance coverage metrics
  const calculateInsuranceMetrics = () => {
    const annualIncome = state.userProfile.monthlyIncome * 12;
    const recommendedTermCoverage = annualIncome * 15; // 15x annual income as a baseline
    const recommendedHealthCoverage = annualIncome * 0.75; // 75% of annual income
    
    const termCoverage = state.userProfile.insurance?.termInsurance || 0;
    const healthCoverage = state.userProfile.insurance?.healthInsurance || 0;
    
    const termCoverageRatio = termCoverage > 0 ? (termCoverage / recommendedTermCoverage) * 100 : 0;
    const healthCoverageRatio = healthCoverage > 0 ? (healthCoverage / recommendedHealthCoverage) * 100 : 0;
    
    const termPremiumRatio = state.userProfile.insurance?.termPremium ? 
      (state.userProfile.insurance.termPremium / (state.userProfile.monthlyIncome * 12)) * 100 : 0;
    const healthPremiumRatio = state.userProfile.insurance?.healthPremium ? 
      (state.userProfile.insurance.healthPremium / (state.userProfile.monthlyIncome * 12)) * 100 : 0;
    
    return {
      termCoverage,
      healthCoverage,
      recommendedTermCoverage,
      recommendedHealthCoverage,
      termCoverageRatio,
      healthCoverageRatio,
      termPremiumRatio,
      healthPremiumRatio,
      totalPremiumRatio: termPremiumRatio + healthPremiumRatio
    };
  };
  
  const insuranceMetrics = calculateInsuranceMetrics();

  // Calculate emergency fund metrics
  const calculateEmergencyFundMetrics = () => {
    const recommendedEmergencyFund = state.userProfile.monthlyExpenses * 6; // 6 months of expenses
    const emergencyFund = state.userProfile.emergencyFund || 0;
    const emergencyFundRatio = emergencyFund > 0 ? (emergencyFund / recommendedEmergencyFund) * 100 : 0;
    const emergencyFundMonths = emergencyFund > 0 ? emergencyFund / state.userProfile.monthlyExpenses : 0;
    
    return {
      emergencyFund,
      recommendedEmergencyFund,
      emergencyFundRatio,
      emergencyFundMonths
    };
  };
  
  const emergencyFundMetrics = calculateEmergencyFundMetrics();

  // Generate smart recommendations with SIP vs Lumpsum guidance
  const smartRecommendations = generateSIPRecommendations(state.goals, state.userProfile, retirementMetrics);

  // Calculate what-if scenarios for all goals and retirement
  const whatIfTotals = calculateTotalWhatIfRequirements(state.goals, retirementMetrics, state.retirementPlan);

  const getRecommendations = () => {
    const recommendations = [...smartRecommendations];

    if (isOverBudget) {
      recommendations.unshift({
        type: 'error',
        title: 'Budget Overflow Alert',
        text: `Required commitment (₹${totalMonthlyCommitment.toLocaleString()}) exceeds your surplus (₹${monthlySurplus.toLocaleString()}). Consider phased approach or goal timeline adjustments.`
      });
    }

    if (retirementMetrics.shortfall > 0 && !isOverBudget) {
      recommendations.push({
        type: 'warning',
        title: 'Retirement Planning Gap',
        text: `Additional ₹${retirementMetrics.shortfall.toLocaleString()}/month needed for retirement. Priority focus required.`
      });
    }

    // SIP vs Lumpsum recommendations for each goal
    state.goals.forEach(goal => {
      if (!goal.requiredSIP && !goal.requiredLumpSum) return;
      
      const sipAmount = (goal.requiredSIP || 0) * 12 * goal.timeHorizon;
      const lumpSumAmount = goal.requiredLumpSum || 0;
      
      if (sipAmount > 0 && lumpSumAmount > 0) {
        const preference = sipAmount < lumpSumAmount ? 'SIP' : 'Lumpsum';
        const savings = Math.abs(sipAmount - lumpSumAmount);
        
        recommendations.push({
          type: 'info',
          title: `${goal.name}: ${preference} Recommended`,
          text: `${preference === 'SIP' ? 'Monthly SIP' : 'Lumpsum investment'} of ₹${preference === 'SIP' ? (goal.requiredSIP || 0).toLocaleString() + '/month' : lumpSumAmount.toLocaleString()} is optimal. Saves ₹${savings.toLocaleString()} vs alternative approach.`
        });
      } else if (sipAmount > 0) {
        recommendations.push({
          type: 'info',
          title: `${goal.name}: SIP Required`,
          text: `Monthly SIP of ₹${(goal.requiredSIP || 0).toLocaleString()} needed to achieve this goal.`
        });
      } else if (lumpSumAmount > 0) {
        recommendations.push({
          type: 'info',
          title: `${goal.name}: Lumpsum Required`,
          text: `One-time investment of ₹${lumpSumAmount.toLocaleString()} needed to achieve this goal.`
        });
      }
    });

    // What-if scenario recommendation
    if (whatIfTotals.difference > 0) {
      recommendations.push({
        type: 'warning',
        title: 'What-If Scenario Planning',
        text: `Consider an additional buffer of ₹${whatIfTotals.difference.toLocaleString()}/month to prepare for potential cost increases (as per your what-if scenarios).`
      });
    }

    // Emergency fund recommendations
    if (emergencyFundMetrics.emergencyFundRatio < 50) {
      recommendations.push({
        type: 'critical',
        title: 'Emergency Fund Priority',
        text: `Your emergency fund covers only ${emergencyFundMetrics.emergencyFundMonths.toFixed(1)} months of expenses. Build up to at least 6 months (₹${emergencyFundMetrics.recommendedEmergencyFund.toLocaleString()}).`
      });
    } else if (emergencyFundMetrics.emergencyFundRatio < 100) {
      recommendations.push({
        type: 'warning',
        title: 'Boost Emergency Fund',
        text: `Your emergency fund is at ${emergencyFundMetrics.emergencyFundRatio.toFixed(0)}% of target. Continue building to reach 6 months of expenses.`
      });
    }

    // Insurance recommendations
    if (insuranceMetrics.termCoverageRatio < 70) {
      recommendations.push({
        type: 'critical',
        title: 'Inadequate Term Insurance',
        text: `Your term insurance covers only ${insuranceMetrics.termCoverageRatio.toFixed(0)}% of recommended amount. Consider increasing coverage to ₹${insuranceMetrics.recommendedTermCoverage.toLocaleString()}.`
      });
    }
    
    if (insuranceMetrics.healthCoverageRatio < 70) {
      recommendations.push({
        type: 'warning',
        title: 'Health Insurance Gap',
        text: `Your health insurance covers only ${insuranceMetrics.healthCoverageRatio.toFixed(0)}% of recommended amount. Consider increasing to ₹${insuranceMetrics.recommendedHealthCoverage.toLocaleString()}.`
      });
    }

    // Income growth advantage
    const incomeGrowth = state.userProfile.incomeGrowthRate || 8;
    const expenseGrowth = state.userProfile.expenseGrowthRate || 6;
    
    if (incomeGrowth > expenseGrowth) {
      recommendations.push({
        type: 'opportunity',
        title: 'Growing Income Advantage',
        text: `Your income grows ${incomeGrowth}% vs expenses at ${expenseGrowth}%. Plan to increase SIP amounts annually to leverage this trend.`
      });
    }

    return recommendations;
  };

  const recommendations = getRecommendations();
  const affordabilityStatus = totalMonthlyCommitment <= monthlySurplus ? 'affordable' : 'challenging';

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <motion.div
      id="financial-summary-print-area"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto"
    >
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-[#19b48d] flex items-center justify-center">
            <SafeIcon icon={FiFileText} className="text-white text-lg" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Financial Plan Summary</h2>
            <p className="text-gray-600">Complete overview of your financial planning strategy</p>
          </div>
        </div>
        <PDFExport />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Key Metrics */}
        <div className="lg:col-span-2 space-y-6">
          {/* Net Worth & Investment Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Financial Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <SafeIcon icon={FiTarget} className="text-blue-600 text-2xl mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(netWorth)}</p>
                <p className="text-sm text-gray-600">Net Worth</p>
              </div>
              <div className="text-center p-4 bg-[#19b48d]/5 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="text-[#19b48d] text-2xl mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">₹{totalMonthlySIP.toLocaleString()}</p>
                <p className="text-sm text-gray-600">Total Monthly SIP</p>
              </div>
              <div className="text-center p-4 bg-[#f58320]/5 rounded-lg">
                <SafeIcon icon={FiActivity} className="text-[#f58320] text-2xl mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">₹{whatIfTotals.totalWhatIfSIP.toLocaleString()}</p>
                <p className="text-sm text-gray-600">What-If SIP Need</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiDollarSign} className="text-purple-600 text-2xl mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">₹{totalMonthlyCommitment.toLocaleString()}</p>
                <p className="text-sm text-gray-600">Total Monthly Commitment</p>
              </div>
            </div>

            {/* Affordability Indicator */}
            <div className={`p-4 rounded-lg border-l-4 ${affordabilityStatus === 'affordable' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
              <div className="flex items-center space-x-3">
                <SafeIcon
                  icon={affordabilityStatus === 'affordable' ? FiCheckCircle : FiAlertTriangle}
                  className={`text-lg ${affordabilityStatus === 'affordable' ? 'text-green-600' : 'text-red-600'}`}
                />
                <div>
                  <p className={`font-medium ${affordabilityStatus === 'affordable' ? 'text-green-900' : 'text-red-900'}`}>
                    {affordabilityStatus === 'affordable' ? 'Plan is Financially Feasible' : 'Plan Exceeds Current Capacity'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Current surplus: ₹{monthlySurplus.toLocaleString()} | Total commitment: ₹{totalMonthlyCommitment.toLocaleString()} | 
                    Utilization: {((totalMonthlyCommitment / monthlySurplus) * 100).toFixed(0)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Insurance and Emergency Fund Analysis */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Protection Strategy</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Insurance Analysis */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <SafeIcon icon={FiShield} className="text-blue-600" />
                  <h4 className="font-medium text-gray-900">Insurance Coverage</h4>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Term Life Insurance:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(insuranceMetrics.termCoverage)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          insuranceMetrics.termCoverageRatio >= 80
                            ? 'bg-green-500'
                            : insuranceMetrics.termCoverageRatio >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(insuranceMetrics.termCoverageRatio, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Recommended: {formatCurrency(insuranceMetrics.recommendedTermCoverage)} ({insuranceMetrics.termCoverageRatio.toFixed(0)}% covered)
                    </p>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Health Insurance:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(insuranceMetrics.healthCoverage)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          insuranceMetrics.healthCoverageRatio >= 80
                            ? 'bg-green-500'
                            : insuranceMetrics.healthCoverageRatio >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(insuranceMetrics.healthCoverageRatio, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Recommended: {formatCurrency(insuranceMetrics.recommendedHealthCoverage)} ({insuranceMetrics.healthCoverageRatio.toFixed(0)}% covered)
                    </p>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Annual Premium Allocation:</span>
                      <span
                        className={`font-medium ${
                          insuranceMetrics.totalPremiumRatio > 10
                            ? 'text-red-600'
                            : insuranceMetrics.totalPremiumRatio > 5
                            ? 'text-yellow-600'
                            : 'text-green-600'
                        }`}
                      >
                        {insuranceMetrics.totalPremiumRatio.toFixed(1)}% of income
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Optimal: 5-10% of annual income for complete protection
                    </p>
                  </div>
                </div>
              </div>

              {/* Emergency Fund Analysis */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <SafeIcon icon={FiAlertTriangle} className="text-orange-500" />
                  <h4 className="font-medium text-gray-900">Emergency Fund</h4>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Current Fund:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(emergencyFundMetrics.emergencyFund)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          emergencyFundMetrics.emergencyFundRatio >= 100
                            ? 'bg-green-500'
                            : emergencyFundMetrics.emergencyFundRatio >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(emergencyFundMetrics.emergencyFundRatio, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Target: {formatCurrency(emergencyFundMetrics.recommendedEmergencyFund)} ({emergencyFundMetrics.emergencyFundRatio.toFixed(0)}% funded)
                    </p>
                  </div>
                  <div className="flex items-center space-x-3 mt-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Coverage:</span>
                        <span
                          className={`font-medium ${
                            emergencyFundMetrics.emergencyFundMonths >= 6
                              ? 'text-green-600'
                              : emergencyFundMetrics.emergencyFundMonths >= 3
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {emergencyFundMetrics.emergencyFundMonths.toFixed(1)} months
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            emergencyFundMetrics.emergencyFundMonths >= 6
                              ? 'bg-green-500'
                              : emergencyFundMetrics.emergencyFundMonths >= 3
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((emergencyFundMetrics.emergencyFundMonths / 6) * 100, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Recommendation: 6 months of expenses
                      </p>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg mt-2">
                    <p className="text-xs text-blue-800">
                      <strong>Pro Tip:</strong> Keep emergency funds in high-yield savings or liquid funds for quick access with reasonable returns.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Overall Protection Score */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700">Protection Strategy Score</h4>
                <span
                  className={`text-lg font-bold ${
                    ((insuranceMetrics.termCoverageRatio + insuranceMetrics.healthCoverageRatio + emergencyFundMetrics.emergencyFundRatio) / 3) >= 80
                      ? 'text-green-600'
                      : ((insuranceMetrics.termCoverageRatio + insuranceMetrics.healthCoverageRatio + emergencyFundMetrics.emergencyFundRatio) / 3) >= 50
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {Math.round((insuranceMetrics.termCoverageRatio + insuranceMetrics.healthCoverageRatio + emergencyFundMetrics.emergencyFundRatio) / 3)}%
                </span>
              </div>
              <div className="w-full bg-white rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full bg-gradient-to-r ${
                    ((insuranceMetrics.termCoverageRatio + insuranceMetrics.healthCoverageRatio + emergencyFundMetrics.emergencyFundRatio) / 3) >= 80
                      ? 'from-green-400 to-green-600'
                      : ((insuranceMetrics.termCoverageRatio + insuranceMetrics.healthCoverageRatio + emergencyFundMetrics.emergencyFundRatio) / 3) >= 50
                      ? 'from-yellow-400 to-yellow-600'
                      : 'from-red-400 to-red-600'
                  }`}
                  style={{
                    width: `${Math.min(
                      ((insuranceMetrics.termCoverageRatio + insuranceMetrics.healthCoverageRatio + emergencyFundMetrics.emergencyFundRatio) / 3),
                      100
                    )}%`
                  }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Protection score combines term insurance, health insurance, and emergency fund adequacy. A score above 80% indicates excellent protection against financial emergencies.
              </p>
            </div>
          </div>

          {/* Goal Status with Performance Analysis */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Goal Achievement Status</h3>
            <div className="space-y-4">
              {/* Retirement Goal Status */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      retirementMetrics.corpusRequired > 0 && 
                      retirementMetrics.corpusGrowth && 
                      retirementMetrics.corpusGrowth.length > 0 && 
                      retirementMetrics.corpusGrowth[retirementMetrics.corpusGrowth.length - 1] >= retirementMetrics.corpusRequired * 0.9 
                        ? 'bg-green-500' 
                        : retirementMetrics.corpusGrowth && 
                          retirementMetrics.corpusGrowth.length > 0 && 
                          retirementMetrics.corpusGrowth[retirementMetrics.corpusGrowth.length - 1] >= retirementMetrics.corpusRequired * 0.7 
                          ? 'bg-[#f58320]' 
                          : 'bg-red-500'}`} />
                    <div>
                      <p className="font-medium text-gray-900">Retirement</p>
                      <p className="text-sm text-gray-600">
                        {state.userProfile.retirementAge - state.userProfile.currentAge} years • 
                        ₹{state.retirementPlan.monthlyIncomeRequired.toLocaleString()}/month • 
                        SIP: ₹{retirementMetrics.requiredSIP?.toLocaleString() || '0'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {retirementMetrics.corpusRequired > 0 && retirementMetrics.corpusGrowth && retirementMetrics.corpusGrowth.length > 0
                        ? Math.round((retirementMetrics.corpusGrowth[retirementMetrics.corpusGrowth.length - 1] / retirementMetrics.corpusRequired) * 100)
                        : 0}%
                    </p>
                    <p className="text-sm text-gray-600">
                      {retirementMetrics.corpusRequired > 0 && 
                       retirementMetrics.corpusGrowth && 
                       retirementMetrics.corpusGrowth.length > 0 && 
                       retirementMetrics.corpusGrowth[retirementMetrics.corpusGrowth.length - 1] >= retirementMetrics.corpusRequired * 0.9 
                        ? 'On Track' 
                        : retirementMetrics.corpusGrowth && 
                          retirementMetrics.corpusGrowth.length > 0 && 
                          retirementMetrics.corpusGrowth[retirementMetrics.corpusGrowth.length - 1] >= retirementMetrics.corpusRequired * 0.7 
                          ? 'Needs Attention' 
                          : 'At Risk'}
                    </p>
                  </div>
                </div>

                {/* Retirement Investment Strategy */}
                <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-2 mb-1">
                    <SafeIcon icon={FiInfo} className="text-blue-600 text-sm" />
                    <span className="text-sm font-medium text-blue-800">Investment Strategy</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    {retirementMetrics.requiredSIP > 0 && retirementMetrics.requiredLumpsum > 0
                      ? (retirementMetrics.requiredSIP * 12 * (state.userProfile.retirementAge - state.userProfile.currentAge)) < retirementMetrics.requiredLumpsum
                        ? `Recommended: Monthly SIP of ₹${retirementMetrics.requiredSIP.toLocaleString()} (saves ₹${(retirementMetrics.requiredLumpsum - retirementMetrics.requiredSIP * 12 * (state.userProfile.retirementAge - state.userProfile.currentAge)).toLocaleString()} vs lumpsum)`
                        : `Recommended: Lumpsum of ₹${retirementMetrics.requiredLumpsum.toLocaleString()} (saves ₹${(retirementMetrics.requiredSIP * 12 * (state.userProfile.retirementAge - state.userProfile.currentAge) - retirementMetrics.requiredLumpsum).toLocaleString()} vs SIP)`
                      : retirementMetrics.requiredSIP > 0
                      ? `Required: Monthly SIP of ₹${retirementMetrics.requiredSIP.toLocaleString()}`
                      : `Required: Lumpsum of ₹${retirementMetrics.requiredLumpsum.toLocaleString()}`}
                  </p>
                </div>

                {/* What-If Scenario Analysis */}
                <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiPercent} className="text-orange-600 text-sm" />
                    <span className="text-sm font-medium text-orange-800">What-If Analysis (+{state.retirementPlan.whatIfPercentage || 40}%)</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                    <div>
                      <span className="font-medium">Standard Corpus:</span> ₹{retirementMetrics.corpusRequired.toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">What-If Corpus:</span> ₹{retirementMetrics.whatIfCorpusRequired?.toLocaleString() || (retirementMetrics.corpusRequired * (1 + (state.retirementPlan.whatIfPercentage || 40)/100)).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">Standard SIP:</span> ₹{retirementMetrics.requiredSIP?.toLocaleString() || '0'}
                    </div>
                    <div>
                      <span className="font-medium">What-If SIP:</span> ₹{retirementMetrics.whatIfRequiredSIP?.toLocaleString() || Math.round((retirementMetrics.requiredSIP || 0) * (1 + (state.retirementPlan.whatIfPercentage || 40)/100)).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Goals */}
              {state.goals.map(goal => {
                // Calculate performance scenarios for each goal
                const performanceScenarios = calculatePerformanceScenarios(goal, state.userProfile);

                // Reuse the goal's own stored what-if metrics (two-phase, monthly
                // compounding) rather than recomputing with a different formula.
                const whatIfPercentage = goal.whatIfPercentage || 40;
                const futureValue = goal.futureValue || 0;
                const whatIfFutureValue = goal.whatIfFutureValue || 0;
                const whatIfSIP = goal.whatIfSIP || 0;

                return (
                  <div key={goal.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${goal.achievementProbability >= 90 ? 'bg-green-500' : goal.achievementProbability >= 70 ? 'bg-[#f58320]' : 'bg-red-500'}`} />
                        <div>
                          <p className="font-medium text-gray-900">{goal.name}</p>
                          <p className="text-sm text-gray-600">
                            {goal.timeHorizon} years • ₹{goal.goalAmount.toLocaleString()}
                            {goal.requiredSIP > 0 && ` • SIP: ₹${goal.requiredSIP.toLocaleString()}`}
                            {goal.requiredLumpSum > 0 && ` • Lumpsum: ₹${goal.requiredLumpSum.toLocaleString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{goal.achievementProbability}%</p>
                        <p className="text-sm text-gray-600">
                          {goal.achievementProbability >= 90 ? 'On Track' : goal.achievementProbability >= 70 ? 'Needs Attention' : 'At Risk'}
                        </p>
                      </div>
                    </div>

                    {/* Investment Strategy Recommendation */}
                    {(goal.requiredSIP > 0 || goal.requiredLumpSum > 0) && (
                      <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center space-x-2 mb-1">
                          <SafeIcon icon={FiInfo} className="text-blue-600 text-sm" />
                          <span className="text-sm font-medium text-blue-800">Investment Strategy</span>
                        </div>
                        <p className="text-sm text-blue-700">
                          {goal.requiredSIP > 0 && goal.requiredLumpSum > 0
                            ? (goal.requiredSIP * 12 * goal.timeHorizon) < goal.requiredLumpSum
                              ? `Recommended: Monthly SIP of ₹${goal.requiredSIP.toLocaleString()} (saves ₹${(goal.requiredLumpSum - goal.requiredSIP * 12 * goal.timeHorizon).toLocaleString()} vs lumpsum)`
                              : `Recommended: Lumpsum of ₹${goal.requiredLumpSum.toLocaleString()} (saves ₹${(goal.requiredSIP * 12 * goal.timeHorizon - goal.requiredLumpSum).toLocaleString()} vs SIP)`
                            : goal.requiredSIP > 0
                            ? `Required: Monthly SIP of ₹${goal.requiredSIP.toLocaleString()}`
                            : `Required: Lumpsum of ₹${goal.requiredLumpSum.toLocaleString()}`}
                        </p>
                      </div>
                    )}

                    {/* Performance Analysis */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <p className="text-xs text-green-600 font-medium">Direct Funds ({goal.expectedReturn + 1}%)</p>
                        <p className="text-sm font-bold text-green-700">
                          +₹{performanceScenarios.directFundsExtraReturns?.toLocaleString() || '0'}
                        </p>
                        <p className="text-xs text-gray-600">{performanceScenarios.outperformanceEarlierBy?.toFixed(1)}y earlier</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-blue-600 font-medium">Current Plan ({goal.expectedReturn}%)</p>
                        <p className="text-sm font-bold text-blue-700">
                          ₹{performanceScenarios.currentPlanReturns?.toLocaleString() || '0'}
                        </p>
                        <p className="text-xs text-gray-600">Target: {goal.timeHorizon} years</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-red-600 font-medium">Regular Funds ({goal.expectedReturn - 1}%)</p>
                        <p className="text-sm font-bold text-red-700">
                          -₹{performanceScenarios.regularFundsLoss?.toLocaleString() || '0'}
                        </p>
                        <p className="text-xs text-gray-600">{performanceScenarios.underperformanceDelayBy?.toFixed(1)}y delay</p>
                      </div>
                    </div>

                    {/* What-If Scenario Analysis */}
                    <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiPercent} className="text-orange-600 text-sm" />
                        <span className="text-sm font-medium text-orange-800">What-If Analysis (+{whatIfPercentage}%)</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                        <div>
                          <span className="font-medium">Standard Future Value:</span> ₹{futureValue.toLocaleString()}
                        </div>
                        <div>
                          <span className="font-medium">What-If Future Value:</span> ₹{whatIfFutureValue.toLocaleString()}
                        </div>
                        <div>
                          <span className="font-medium">Standard SIP:</span> ₹{goal.requiredSIP?.toLocaleString() || '0'}
                        </div>
                        <div>
                          <span className="font-medium">What-If SIP:</span> ₹{Math.round(whatIfSIP).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Monthly Cashflow */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Monthly Cashflow Analysis</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly Income</span>
                <span className="font-semibold text-green-600">₹{state.userProfile.monthlyIncome.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly Expenses</span>
                <span className="font-semibold text-red-600">₹{state.userProfile.monthlyExpenses.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Available Surplus</span>
                <span className="font-semibold text-blue-600">₹{monthlySurplus.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total SIP (Goals + Retirement)</span>
                <span className="font-semibold text-purple-600">₹{totalMonthlySIP.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">What-If SIP Requirement</span>
                <span className="font-semibold text-orange-600">₹{whatIfTotals.totalWhatIfSIP.toLocaleString()}</span>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-900 font-medium">Remaining After All Commitments</span>
                  <span className={`font-bold ${monthlySurplus - totalMonthlyCommitment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₹{(monthlySurplus - totalMonthlyCommitment).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Total Commitment Utilization</span>
                  <span
                    className={`text-sm font-medium ${
                      totalMonthlyCommitment > monthlySurplus
                        ? 'text-red-600'
                        : totalMonthlyCommitment > monthlySurplus * 0.8
                        ? 'text-[#f58320]'
                        : 'text-green-600'
                    }`}
                  >
                    {monthlySurplus > 0 ? ((totalMonthlyCommitment / monthlySurplus) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      totalMonthlyCommitment > monthlySurplus
                        ? 'bg-red-500'
                        : totalMonthlyCommitment > monthlySurplus * 0.8
                        ? 'bg-[#f58320]'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min((totalMonthlyCommitment / monthlySurplus) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: Keep below 80% | Ideal: 50-70%
                </p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">What-If Scenario Utilization</span>
                  <span
                    className={`text-sm font-medium ${
                      whatIfTotals.totalWhatIfSIP > monthlySurplus
                        ? 'text-red-600'
                        : whatIfTotals.totalWhatIfSIP > monthlySurplus * 0.8
                        ? 'text-[#f58320]'
                        : 'text-green-600'
                    }`}
                  >
                    {monthlySurplus > 0 ? ((whatIfTotals.totalWhatIfSIP / monthlySurplus) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      whatIfTotals.totalWhatIfSIP > monthlySurplus
                        ? 'bg-red-500'
                        : whatIfTotals.totalWhatIfSIP > monthlySurplus * 0.8
                        ? 'bg-[#f58320]'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min((whatIfTotals.totalWhatIfSIP / monthlySurplus) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Buffer required: ₹{(whatIfTotals.totalWhatIfSIP - totalMonthlySIP).toLocaleString()}/month
                </p>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <AIInsights
            userProfile={state.userProfile}
            goals={state.goals}
            totalRequiredSIP={totalMonthlyCommitment}
            monthlySurplus={monthlySurplus}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Overall Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Status</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiCheckCircle} className="text-green-500 text-lg" />
                <div>
                  <p className="font-medium text-gray-900">{goalsOnTrack.length} Goals On Track</p>
                  <p className="text-sm text-gray-600">90-100% affordability score</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiAlertTriangle} className="text-[#f58320] text-lg" />
                <div>
                  <p className="font-medium text-gray-900">{goalsNeedingAttention.length} Need Attention</p>
                  <p className="text-sm text-gray-600">70-89% affordability score</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiAlertTriangle} className="text-red-500 text-lg" />
                <div>
                  <p className="font-medium text-gray-900">{goalsAtRisk.length} At Risk</p>
                  <p className="text-sm text-gray-600">&lt;70% affordability score</p>
                </div>
              </div>
            </div>

            {/* Financial Health Score */}
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Financial Health Score</span>
                <span className="text-lg font-bold text-indigo-600">
                  {Math.round((goalsOnTrack.length / Math.max(state.goals.length, 1)) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
                  style={{ width: `${(goalsOnTrack.length / Math.max(state.goals.length, 1)) * 100}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Share of goals with a 90%+ affordability score
              </p>
            </div>
          </div>

          {/* Smart Recommendations */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Smart Recommendations</h3>
            <div className="space-y-4">
              {recommendations.map((rec, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-l-4 ${
                    rec.type === 'error'
                      ? 'bg-red-50 border-red-500'
                      : rec.type === 'warning'
                      ? 'bg-[#f58320]/5 border-[#f58320]'
                      : rec.type === 'success'
                      ? 'bg-green-50 border-green-500'
                      : rec.type === 'strategy'
                      ? 'bg-purple-50 border-purple-500'
                      : rec.type === 'immediate'
                      ? 'bg-blue-50 border-blue-500'
                      : rec.type === 'future'
                      ? 'bg-indigo-50 border-indigo-500'
                      : rec.type === 'modify'
                      ? 'bg-yellow-50 border-yellow-500'
                      : rec.type === 'opportunity'
                      ? 'bg-emerald-50 border-emerald-500'
                      : rec.type === 'info'
                      ? 'bg-cyan-50 border-cyan-500'
                      : rec.type === 'critical'
                      ? 'bg-red-50 border-red-500'
                      : 'bg-gray-50 border-gray-500'
                  }`}
                >
                  {rec.title && (
                    <p className="font-medium text-sm text-gray-900 mb-1">{rec.title}</p>
                  )}
                  <p className="text-sm text-gray-700">{rec.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Key Assumptions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Assumptions</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Short-term returns:</span>
                <span className="font-medium">{state.userProfile.returnExpectations?.shortTerm || 7}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Medium-term returns:</span>
                <span className="font-medium">{state.userProfile.returnExpectations?.mediumTerm || 10}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Long-term returns:</span>
                <span className="font-medium">{state.userProfile.returnExpectations?.longTerm || 12}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">General inflation:</span>
                <span className="font-medium">{state.userProfile.inflationRates?.general || 6}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Education inflation:</span>
                <span className="font-medium">{state.userProfile.inflationRates?.education || 8}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Medical inflation:</span>
                <span className="font-medium">{state.userProfile.inflationRates?.medical || 10}%</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600">
                All projections are based on user inputs and market expectations. Investment instruments assumed: Direct mutual funds. Past performance does not guarantee future results. What-if scenarios assume goal cost increases of {state.goals.map(g => g.whatIfPercentage || 40).reduce((sum, val) => sum + val, 0) / Math.max(state.goals.length, 1)}% on average.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Summary;